'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { AppRole } from '@/hooks/useRolePermissions';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const supabase = createClient();

  const fetchProfile = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (data) {
        setUserProfile(data);
        setUserRole(data.role as AppRole);
      } else {
        // No profile row found — treat as admin fallback so UI doesn't hang
        setUserRole('' as AppRole);
      }
    } catch {
      // Profile fetch failed — set role to empty string so consumers
      // don't wait forever on null. The leads page treats '' as admin.
      setUserRole('' as AppRole);
    }
  };

  const clearAuthState = () => {
    setUser(null);
    setSession(null);
    setUserProfile(null);
    setUserRole(null);
  };

  useEffect(() => {
    let isMounted = true;

    // onAuthStateChange fires INITIAL_SESSION once the session is loaded
    // from cookie/localStorage. IMPORTANT: this callback must return
    // quickly — the Supabase client blocks signInWithPassword and other
    // auth methods until all onAuthStateChange callbacks resolve, so
    // awaiting network calls here causes a deadlock.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!isMounted) return;

      if (newSession?.user) {
        setUser(newSession.user);
        setSession(newSession);
      } else {
        clearAuthState();
      }
      setLoading(false);
    });

    // Safety net: if INITIAL_SESSION hasn't fired within 4 s
    // (e.g. cookie storage is completely empty or broken),
    // turn off loading so the UI isn't stuck on a spinner forever.
    const safetyTimeout = setTimeout(() => {
      if (isMounted) setLoading(false);
    }, 4000);

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the user profile whenever the authenticated user changes.
  // Kept in a separate effect so it doesn't block onAuthStateChange.
  useEffect(() => {
    if (user?.id) {
      fetchProfile(user.id);
    } else {
      setUserProfile(null);
      setUserRole(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;

    // onAuthStateChange will fire and set user/session state, which
    // in turn triggers the profile-fetch effect. We also set eagerly
    // here so the caller can navigate immediately.
    if (data.user) {
      setUser(data.user);
      setSession(data.session);
    }

    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    clearAuthState();
  };

  /**
   * getCurrentUser — always performs a server-side JWT validation.
   * Use this wherever you need a guaranteed-fresh, server-verified identity.
   */
  const getCurrentUser = async () => {
    const {
      data: { user: validatedUser },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return validatedUser;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    userProfile,
    userRole,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
